import type { AnyConnectionType } from '@precon-tools-suite-by-shared/precon-graphql-types';
import type {
    ApolloQueryResult,
    ApolloClient,
    NormalizedCacheObject,
    OperationVariables,
    FetchMoreQueryOptions,
} from '@apollo/client/core';
import { type MaybeRefOrGetter, toValue, type Ref, ref, nextTick } from 'vue';
import type {
    DocumentParameter,
    VariablesParameter,
    OptionsParameter,
    UseQueryReturn,
} from '@vue/apollo-composable/dist/useQuery';

import {
    provideApolloClient,
    useQuery,
    useLazyQuery,
} from '@vue/apollo-composable';

import { wait } from '@precon-tools-suite-by-shared/js-utils';

import {
    type UseConnectionWithPlaceholdersOptions,
    type UseConnectionWithPlaceholdersResult,
    useConnectionWithPlaceholders,
} from './useConnectionWithPlaceholders';

export type ForwardPagerVariables = { first?: number; after?: string };

export type BackwardPagerVariables = { last?: number; before?: string };

export type UsePagerQueryVariables = OperationVariables &
    ForwardPagerVariables &
    BackwardPagerVariables;

/**
 * The options for the `usePagerQuery` composable.
 *
 * @see usePagerQuery
 */
export type UsePagerQueryOptions<
    TConnection extends AnyConnectionType,
    TQueryResult,
    TQueryVariables extends UsePagerQueryVariables
> = UseConnectionWithPlaceholdersOptions & {
    /**
     * Function that extracts the connection to be paged from the query result.
     *
     * Will only be called after the query has finished.
     *
     * @param queryResult The result from the query.
     */
    connection: (queryResult: TQueryResult) => TConnection | null | undefined;
    /**
     * The typed query document to get the connection.
     */
    queryDocument: DocumentParameter<TQueryResult, TQueryVariables>;
    /**
     * The initial parameters for the query.
     */
    queryVariables?: VariablesParameter<TQueryVariables>;
    /**
     * Extra options that will be passed to the options parameter of `useQuery`.
     */
    queryOptions?: OptionsParameter<TQueryResult, TQueryVariables>;
    /**
     * The apollo client that is used to fetch the query. Defaults to the default apollo client of the application.
     */
    client?: ApolloClient<NormalizedCacheObject>;
    /**
     * Whether to use `useLazyQuery` instead of `useQuery`.
     *
     * @default false
     */
    lazy?: boolean;
    /**
     * The amount of time in miliseconds until the next page will be fetched after the previous was loaded.
     *
     * Defaults to one vue-tick. This default behaviour will be used if the result is non-positive.
     */
    fetchPageTimeout?: MaybeRefOrGetter<number | null | undefined>;
};

/**
 * The type for the `fetchNextPage` helper function of the `usePagerQuery` composable.
 *
 * @see UsePagerQueryResult
 */
export interface FetchPage<
    TQueryResult,
    TQueryVariables extends UsePagerQueryVariables
> {
    <
        TNextQueryResult = TQueryResult,
        TNextQueryVariables extends UsePagerQueryVariables = TQueryVariables
    >(
        options?: FetchMoreQueryOptions<TNextQueryVariables, TNextQueryResult>
    ): Promise<ApolloQueryResult<TNextQueryResult> | null>;
}

/**
 * The result type of the `usePagerQuery` composable.
 *
 * @see usePagerQuery
 */
export type UsePagerQueryResult<
    TConnection extends AnyConnectionType,
    TQueryResult,
    TQueryVariables extends UsePagerQueryVariables
> = UseConnectionWithPlaceholdersResult<TConnection> &
    UseQueryReturn<TQueryResult, TQueryVariables> & {
        /**
         * Starts the query when lazy was set to true. Does nothing otherwise.
         */
        load: () => void;
        /**
         * Helper function to load the next page of a connection.
         */
        fetchNextPage: FetchPage<TQueryResult, TQueryVariables>;
        /**
         * Whether a next page is loading. Any call to `fetchNextPage` will be ignored while this
         * value is `true`.
         */
        nextPageLoading: Readonly<Ref<boolean>>;
        /**
         * Helper function to load the next page of a connection.
         */
        fetchPreviousPage: FetchPage<TQueryResult, TQueryVariables>;
        /**
         * Whether a next page is loading. Any call to `fetchNextPage` will be ignored while this
         * value is `true`.
         */
        previousPageLoading: Readonly<Ref<boolean>>;
    };

/**
 * Performs a query for a connection and provides some helper function to page through this query.
 *
 * @param options The options for this composable.
 */
export function usePagerQuery<
    TConnection extends AnyConnectionType,
    TQueryResult,
    TQueryVariables extends UsePagerQueryVariables
>(
    options: UsePagerQueryOptions<TConnection, TQueryResult, TQueryVariables>
): UsePagerQueryResult<TConnection, TQueryResult, TQueryVariables> {
    // Provide the client if set.
    if (options.client) provideApolloClient(options.client);

    // Perform the query.
    const query = options.lazy
        ? useLazyQuery<TQueryResult, TQueryVariables>(
              options.queryDocument,
              options.queryVariables,
              options.queryOptions
          )
        : useQuery<TQueryResult, TQueryVariables>(
              options.queryDocument,
              options.queryVariables as VariablesParameter<TQueryVariables>,
              options.queryOptions as OptionsParameter<
                  TQueryResult,
                  TQueryVariables
              >
          );

    // Interpret the query as a connection.
    const connection = useConnectionWithPlaceholders(() => {
        const result = query.result.value;
        if (!result) return undefined;
        return options.connection(result);
    }, options);

    // Helper function that waits for the provided timeout.
    async function fetchMoreTimeout(): Promise<void> {
        const timeout = toValue(options.fetchPageTimeout);
        if (!timeout || timeout <= 0) await nextTick();
        else await wait(timeout);
    }

    // Whether the next page is loading.
    const nextPageLoading = ref<boolean>(false);

    // Helper function to fetch the next page of the connection (if available).
    const fetchNextPage: FetchPage<TQueryResult, TQueryVariables> = async (
        options = {}
    ) => {
        if (!connection.hasNextPage.value || nextPageLoading.value) {
            return null;
        }

        nextPageLoading.value = true;
        try {
            return (await query.fetchMore({
                ...options,
                variables: {
                    after: connection.endCursor.value,
                    ...(options.variables ?? {}),
                } as any, // Because of some annoying type anomalies that I don't bother fixing this internal function.
            })) as any; // Same here,
        } finally {
            // Wait for the timeout before setting nextPageLoading back to false.
            await fetchMoreTimeout();
            nextPageLoading.value = false;
        }
    };

    // Whether the previous page is loading.
    const previousPageLoading = ref<boolean>(false);

    // Helper function to fetch the previous page of the connection (if available).
    const fetchPreviousPage: FetchPage<TQueryResult, TQueryVariables> = async (
        options = {}
    ) => {
        if (!connection.hasPreviousPage.value || previousPageLoading.value)
            return null;

        previousPageLoading.value = true;
        try {
            return (await query.fetchMore({
                ...options,
                variables: {
                    before: connection.startCursor.value,
                    ...(options.variables ?? {}),
                } as any, // Because of some annoying type anomilies that I don't bother fixing this internal function.
            })) as any; // Same here,
        } finally {
            // Wait for the timeout before the setting nextPageLoading back to true.
            await fetchMoreTimeout();
            previousPageLoading.value = false;
        }
    };

    // Mimic the `useLazyQuery` behaviour.
    function load() {
        if ((query as any).load) {
            (query as any).load();
        }
    }

    return Object.assign(connection, query, {
        load,
        fetchNextPage,
        nextPageLoading,
        previousPageLoading,
        fetchPreviousPage,
    });
}
